import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  Sparkles,
  MapPin,
} from "lucide-react";
import { BRAND, SERVICES, IMAGES } from "@/lib/site";
import { getCity, CITIES } from "@/lib/locations";
import { SERVICE_DETAILS } from "@/lib/service-details";
import ServiceIcon from "@/components/site/ServiceIcon";
import ContactForm from "@/components/site/ContactForm";
import Seo from "@/components/site/Seo";
import Breadcrumbs from "@/components/site/Breadcrumbs";
import { logAttribution } from "@/lib/tracking";

/**
 * MONEY PAGE: /services/:serviceSlug/:citySlug
 * Programmatic merge of service × city — unique copy via dynamic composition,
 * not spun. Each page targets the exact high-intent keyword:
 *   "<service> in <city> FL"
 * e.g. "Alzheimer's Care in Naples, FL"
 */
export default function MoneyPage() {
  const { serviceSlug, citySlug } = useParams();
  const service = SERVICES.find((s) => s.slug === serviceSlug);
  const detail = SERVICE_DETAILS[serviceSlug];
  const city = getCity(citySlug);

  const path = `/services/${serviceSlug}/${citySlug}`;
  const title = service && city ? `${service.title} in ${city.name}, FL` : "";
  const desc =
    service && city && detail
      ? `${service.title} in ${city.name}, Florida. ${detail.lead} Serving ${city.neighborhoods}. Vetted caregivers, 24/7 availability. Call ${BRAND.phone} or schedule a free consultation.`
      : "";

  const jsonLd = useMemo(
    () =>
      service && city && detail
        ? {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: service.title,
            name: `${service.title} in ${city.name}, FL`,
            description: detail.lead,
            provider: {
              "@type": "LocalBusiness",
              name: BRAND.fullName,
              telephone: "+1-239-834-4377",
              address: {
                "@type": "PostalAddress",
                addressLocality: city.name,
                addressRegion: "FL",
                addressCountry: "US",
              },
            },
            areaServed: {
              "@type": "City",
              name: city.name,
              containedInPlace: { "@type": "State", "name": "Florida" },
            },
            url: `https://yourelitehomecare.com${path}`,
            offers: { "@type": "Offer", priceRange: "$$$" },
            mainEntityOfPage: {
              "@type": "FAQPage",
              mainEntity: [
                ...detail.faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q.replace(/\?/, "") + ` in ${city.name}?`,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
                {
                  "@type": "Question",
                  name: city.faqExtra.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: city.faqExtra.answer,
                  },
                },
              ],
            },
          }
        : null,
    [service, city, detail, path]
  );

  if (!service || !detail || !city) return <Navigate to="/services" replace />;

  const otherCities = CITIES.filter((c) => c.slug !== city.slug);
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);

  return (
    <div data-testid={`page-money-${serviceSlug}-${citySlug}`}>
      <Seo path={path} title={title} description={desc} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={IMAGES.hero}
            alt={`${service.title} in ${city.name}, FL — Elite Home Care by Hortense caregiver with an elderly client`}
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/85 to-navy-900/35" />
        </div>
        <div className="container-x pt-20 pb-24 md:pt-24 md:pb-28 text-white">
          <Breadcrumbs
            dark
            crumbs={[
              { name: "Home", path: "/" },
              { name: "Services", path: "/services" },
              { name: service.title, path: `/services/${service.slug}` },
              { name: city.name },
            ]}
          />
          <div className="mt-6 flex items-start gap-5">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 border border-white/20 text-gold-300">
              <ServiceIcon name={service.icon} className="h-7 w-7" />
            </span>
            <div>
              <h1 className="font-serif text-5xl md:text-6xl leading-[1.03] max-w-3xl text-white drop-shadow-[0_2px_12px_rgba(10,25,47,0.6)]">
                {service.title} in{" "}
                <span className="italic text-gold-300">{city.name}, FL</span>
              </h1>
              <p className="mt-6 max-w-2xl text-white/90 text-lg leading-relaxed">
                {detail.lead} Proudly serving {city.neighborhoods}.
              </p>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/contact"
              onClick={() => logAttribution(`money_hero_${serviceSlug}_${citySlug}`)}
              data-testid={`money-${serviceSlug}-${citySlug}-cta`}
              className="btn-primary"
            >
              Schedule a Free Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={BRAND.phoneHref}
              onClick={() => logAttribution(`money_hero_call_${citySlug}`)}
              className="btn-ghost-light"
            >
              <Phone className="h-4 w-4" strokeWidth={1.75} /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* LOCAL + INCLUDED */}
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-14">
          <div>
            <span className="overline">
              {service.title} • {city.name}
            </span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Why {city.name} families trust Hortense's team.
            </h2>
            <p className="mt-6 text-navy-700 text-lg leading-relaxed">
              {city.intro}
            </p>
            <p className="mt-4 text-navy-600 leading-relaxed">
              {city.lifestyleParagraph} For {service.title.toLowerCase()}, our
              caregivers in {city.name} are specifically trained, matched to
              your loved one's personality, and supported by a dedicated care
              coordinator you can reach any time.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {city.whyLocal.map((line) => (
                <li key={line} className="flex items-start gap-3 text-navy-800">
                  <CheckCircle2
                    className="h-5 w-5 text-gold-500 mt-0.5 shrink-0"
                    strokeWidth={1.6}
                  />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="card-soft p-8 bg-navy-50/60 h-fit">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-500">
              <Sparkles className="h-3.5 w-3.5" />
              What's included
            </div>
            <h3 className="mt-3 font-serif text-2xl text-navy-900">
              Every detail of {service.title.toLowerCase()}
            </h3>
            <ul className="mt-5 space-y-3">
              {detail.includes.map((line) => (
                <li key={line} className="flex items-start gap-3 text-navy-800">
                  <CheckCircle2
                    className="h-4 w-4 text-gold-500 mt-1 shrink-0"
                    strokeWidth={1.6}
                  />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-navy-900/10">
              <div className="text-xs uppercase tracking-[0.2em] text-navy-500">
                Ideal for
              </div>
              <p className="mt-2 text-navy-700 leading-relaxed">
                {detail.idealFor}
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-navy-50/50 border-y border-navy-900/5">
        <div className="container-x max-w-4xl">
          <span className="overline">
            {service.title} in {city.name} — FAQ
          </span>
          <h2 className="text-4xl md:text-5xl leading-tight">
            Honest answers, no sales pitch.
          </h2>
          <div className="mt-10 space-y-4">
            {[...detail.faqs, city.faqExtra && { q: city.faqExtra.question, a: city.faqExtra.answer }]
              .filter(Boolean)
              .map((f, i) => (
                <details
                  key={f.q}
                  data-testid={`money-faq-${i}`}
                  className="card-soft p-6 md:p-8 group [&_summary]:cursor-pointer"
                >
                  <summary className="flex items-start justify-between gap-4 list-none">
                    <span className="font-serif text-xl text-navy-900">{f.q}</span>
                    <ArrowRight className="h-5 w-5 text-gold-500 mt-1 transition-transform group-open:rotate-90 shrink-0" />
                  </summary>
                  <p className="mt-4 text-navy-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
          </div>
        </div>
      </section>

      {/* CROSS-SELL: other services + other cities */}
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <span className="overline">Other services in {city.name}</span>
            <h3 className="text-3xl leading-tight">Everything we offer locally</h3>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {otherServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}/${city.slug}`}
                  data-testid={`money-other-service-${s.slug}`}
                  className="card-soft p-4 group flex items-center gap-3"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-navy-900 text-gold-300 group-hover:bg-gold-400 group-hover:text-navy-900 transition-colors">
                    <ServiceIcon name={s.icon} className="h-4 w-4" />
                  </span>
                  <span className="text-sm text-navy-900 font-medium">
                    {s.title} in {city.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <span className="overline">
              {service.title} in other SWFL cities
            </span>
            <h3 className="text-3xl leading-tight">Serving all of Southwest Florida</h3>
            <div className="mt-6 flex flex-wrap gap-2">
              {otherCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/services/${service.slug}/${c.slug}`}
                  data-testid={`money-other-city-${c.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-full bg-white border border-navy-900/10 hover:border-gold-400 hover:text-gold-600 transition-colors text-navy-800"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  {service.title} in {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA + FORM */}
      <section className="section bg-navy-50/40 border-t border-navy-900/5">
        <div className="container-x grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <span className="overline">Start the conversation</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Speak with a<br />
              <span className="italic text-gold-500">
                {city.name} care specialist.
              </span>
            </h2>
            <p className="mt-5 text-navy-600 text-lg leading-relaxed">
              Free, no-obligation 20-minute call. Prefer to talk now?
            </p>
            <a
              href={BRAND.phoneHref}
              onClick={() => logAttribution(`money_form_call_${citySlug}`)}
              className="inline-flex items-center gap-3 mt-6 text-navy-900 hover:text-gold-500 transition-colors"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-navy-900 text-gold-300">
                <Phone className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <span>
                <span className="block text-sm text-navy-500">Call us</span>
                <span className="font-serif text-2xl">{BRAND.phone}</span>
              </span>
            </a>
          </div>
          <div className="lg:col-span-3">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
